import Model from '@/store/plugins/orm/Model';
import i18n from '@/i18n';

export class Client extends Model {
    static get entity () {
        return 'clients'
    }

    static fields () {
        return {
            id: this.attr(null),
            name: this.attr(null),
            cost_center: this.attr(null),
            address: this.attr(null),
            city: this.attr(null),
            postal_number: this.attr(null),
            country: this.attr(null),
            card: this.attr(null),
            comment: this.attr(null),
            discount_percent: this.attr('0.00', value => _.round(value, 2).toFixed(2)),
            phone: this.attr(null),
            email: this.attr(null),
            tax_id_number: this.attr(null),
            registration_number: this.attr(null),
            vat_number: this.attr(null),
            id_type: this.attr(null),
            id_number: this.attr(null),
            tax_id_card_number: this.attr(null),
            debt: this.attr('0.0000', value => _.round(value, 4).toFixed(4)),
            pricelist_id: this.attr(null),
        }
    }

    getDiscountPercent() {
        return parseFloat(this.discount_percent);
    }

    getDebt() {
        return Money.parse(this.debt);
    }

    addPayment(value) {
        this.debt = this.getDebt().subtract(Money.parse(value)).toFormat('0.0000')
    }

    isDiplomat() {
        return !!this.tax_id_card_number || this.id_type === 'resident_number_diplomat';
    }

    static idTypes() {
        const idTypes = {
            'me': [
                { id: 'tax_id_number', text: i18n.t('clients.id_types.tax_id_number'), prefix: null },
                { id: 'personal_id_number', text: i18n.t('clients.id_types.personal_id_number'), prefix: null },
                { id: 'passport_number', text: i18n.t('clients.id_types.passport_number'), prefix: null },
                { id: 'vat_number', text: i18n.t('clients.id_types.vat_number'), prefix: null },
                { id: 'tax_number', text: i18n.t('clients.id_types.tax_number'), prefix: null },
                { id: 'social_security_number', text: i18n.t('clients.id_types.social_security_number'), prefix: null },
            ],
            'sr': [
                { id: 'tax_id_number_local', text: i18n.t('clients.id_types.tax_id_number_local'), prefix: '10' },
                { id: 'personal_id_number_local', text: i18n.t('clients.id_types.personal_id_number_local'), prefix: '11' },
                { id: 'tax_id_number_local_jbkjs', text: i18n.t('clients.id_types.tax_id_number_local_jbkjs'), prefix: '12' },
                { id: 'retiree_card_number', text: i18n.t('clients.id_types.retiree_card_number'), prefix: '13' },
                { id: 'agricultural_holding_tax_id_number', text: i18n.t('clients.id_types.agricultural_holding_tax_id_number'), prefix: '14' },
                { id: 'agricultural_holding_personal_id_number', text: i18n.t('clients.id_types.agricultural_holding_personal_id_number'), prefix: '15' },
                { id: 'agricultural_holding_id_number', text: i18n.t('clients.id_types.agricultural_holding_id_number'), prefix: '16' },
                { id: 'personal_id_card_number', text: i18n.t('clients.id_types.personal_id_card_number'), prefix: '20' },
                { id: 'resident_number_refugee', text: i18n.t('clients.id_types.resident_number_refugee'), prefix: '21' },
                { id: 'resident_number_foreign', text: i18n.t('clients.id_types.resident_number_foreign'), prefix: '22' },
                { id: 'passport_number_local', text: i18n.t('clients.id_types.passport_number_local'), prefix: '23' },
                { id: 'passport_number_foreign', text: i18n.t('clients.id_types.passport_number_foreign'), prefix: '30' },
                { id: 'resident_number_diplomat', text: i18n.t('clients.id_types.resident_number_diplomat'), prefix: '31' },
                { id: 'personal_id_card_number_mkd', text: i18n.t('clients.id_types.personal_id_card_number_mkd'), prefix: '32' },
                { id: 'personal_id_card_number_mne', text: i18n.t('clients.id_types.personal_id_card_number_mne'), prefix: '33' },
                { id: 'personal_id_card_number_alb', text: i18n.t('clients.id_types.personal_id_card_number_alb'), prefix: '34' },
                { id: 'personal_id_card_number_bih', text: i18n.t('clients.id_types.personal_id_card_number_bih'), prefix: '35' },
                { id: 'personal_id_card_number_eur', text: i18n.t('clients.id_types.personal_id_card_number_eur'), prefix: '36' },
                { id: 'tax_id_number_foreign', text: i18n.t('clients.id_types.tax_id_number_foreign'), prefix: '40' },
            ],
            'ba': [
                { id: 'tax_id_number', text: i18n.t('clients.id_types.tax_id_number'), prefix: null },
            ],
            'general': [
                { id: 'personal_id_number', text: i18n.t('clients.id_types.personal_id_number'), prefix: null },
                { id: 'passport_number', text: i18n.t('clients.id_types.passport_number'), prefix: null },
                { id: 'vat_number', text: i18n.t('clients.id_types.vat_number'), prefix: null },
                { id: 'tax_number', text: i18n.t('clients.id_types.tax_number'), prefix: null },
                { id: 'social_security_number', text: i18n.t('clients.id_types.social_security_number'), prefix: null },
            ],
        }

        if(idTypes[locale]) return idTypes[locale]

        return idTypes.general
    }

    getFullIdNumber() {
        if(locale !== 'sr') return this.id_number || this.tax_id_number;

        const idType = Client.idTypes().find(type => type.id === this.id_type)
        if(!idType) return '';

        return `${idType.prefix}:${this.id_number}`;
    }

    getTaxIdNumber() {
        if(this.id_type === 'tax_id_number' || this.id_type === 'tax_id_number_local')
            return this.id_number;
        if(this.id_type === 'tax_id_number_local_jbkjs')
            return this.id_number ? this.id_number.split(':')[0] : null;

        return this.tax_id_number || null;
    }

    einvoiceAllowed() {
        if(locale !== 'sr') return false;

        return (this.id_type === 'tax_id_number_local' || this.id_type === 'tax_id_number_local_jbkjs' || this.id_type === 'agricultural_holding_tax_id_number')
            && !!this.id_number
            && (!!this.registration_number || this.id_type === 'agricultural_holding_tax_id_number')
            && !!this.city
            && !!this.postal_number
            && !!this.country;
    }

    /**
     * Make a copy of the Client.
     */
    $duplicate() {
        return new Client(this.$toJson());
    }

    matches(query) {
        const queryText = query.toLowerCase();
        return this.name && this.name.toLowerCase().indexOf(queryText) > -1
            || (this.tax_id_number && this.tax_id_number?.toLowerCase().indexOf(queryText) > -1)
            || (this.id_number && this.id_number.toLowerCase().indexOf(queryText) > -1)
            || (this.registration_number && this.registration_number.toLowerCase().indexOf(queryText) > -1)
            || (this.card && this.card.toLowerCase().indexOf(queryText) > -1);
    }

    static search(query) {
        return Client.all().filter(client => client.matches(query));
    }
}

export default Client
